import { CountingSchemes } from '@ahaslides-product/frontend-toolkit'
import sentry from '@/services/sentry'
import config from '@/config'
import { currentUtcMonth } from '@/util/monthlyParticipant'

/**
 * A factory (not a plain object) so every Vuex.Store instance — including
 * the isolated stores created per test — gets its own state rather than
 * sharing one module-scoped object.
 */
const createState = () => ({
  subscribed: false,
  scheme: null,
  trialActive: false,
})

/**
 * Non-production only: lets QA preview any meter state (e.g. "N left", or the
 * 80%/warning and 90%/danger fill colours) without a real usage history, by
 * appending ?mppUsed=<n> to the URL. Returns NaN in production or when the
 * param is absent/invalid, so the real live count is used untouched.
 */
const readUsedPreviewOverride = () => {
  if (config.env === 'production') return NaN
  try {
    const raw = new URLSearchParams(window.location.search).get('mppUsed')
    if (raw === null) return NaN
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : NaN
  } catch (e) {
    return NaN
  }
}

const getters = {
  used: (state, _, __, rootGetters) => {
    const preview = readUsedPreviewOverride()
    if (Number.isFinite(preview)) {
      return preview
    }
    return state.scheme ? rootGetters['counting/total'](state.scheme) ?? 0 : 0
  },
  limit: (_, __, ___, rootGetters) => rootGetters.monthlyParticipantLimit,
  isVisible: (_, __, rootState, rootGetters) => (
    Number.isFinite(rootGetters.monthlyParticipantLimit) && Boolean(rootState.currentUser && rootState.currentUser.id)
  ),
  trialActive: (state) => state.trialActive,
}

const mutations = {
  SET_SUBSCRIPTION(state, scheme) {
    Object.assign(state, { subscribed: true, scheme })
  },
  RESET_SUBSCRIPTION(state) {
    Object.assign(state, { subscribed: false, scheme: null })
  },
  SET_TRIAL_ACTIVE(state, v) {
    Object.assign(state, { trialActive: Boolean(v) })
  },
}

const actions = {
  /**
   * Subscribes the presenter's monthly participant count through the counting
   * module, which seeds it over HTTP; `counting/reseed` re-reads it after an
   * MQTT reconnect (src/mqtt/subscribers.js).
   * Idempotent so every meter reader (plan card, header) can safely dispatch
   * it on mount while only one subscription is ever created.
   *
   * No-ops (without locking) until the treatment-arm is ready — i.e. a
   * finite monthlyParticipantLimit and a logged-in user — so it can be
   * re-dispatched once planBenefit/currentUser arrive late, and never
   * builds a key with an undefined userId.
   *
   * The subscription lock (SET_SUBSCRIPTION) is committed SYNCHRONOUSLY.
   * Two meters (header + card) can both dispatch init in the same tick;
   * without a synchronous lock both would pass the readiness check and both
   * would subscribe, and a single teardown would then leave the count
   * subscribed.
   */
  async init({
    state, commit, dispatch, rootState, rootGetters,
  }) {
    if (state.subscribed) return
    const isReady = Number.isFinite(rootGetters.monthlyParticipantLimit)
      && Boolean(rootState.currentUser && rootState.currentUser.id)
    if (!isReady) return

    const scheme = CountingSchemes.participantCountPerPresenterPerMonth({
      ownerId: rootState.currentUser.id,
      month: currentUtcMonth(),
    })
    commit('SET_SUBSCRIPTION', scheme)
    await dispatch('counting/subscribe', { keys: [scheme], onError: sentry.captureException }, { root: true })
  },

  /**
   * Tears down the shared subscription. Not called by either meter on
   * unmount — the header keeps it alive app-wide — but kept available for
   * callers (e.g. logout) that need to release it explicitly.
   */
  async teardown({ state, commit, dispatch }) {
    if (!state.subscribed) return
    await dispatch('counting/unsubscribe', { keys: [state.scheme] }, { root: true })
    commit('RESET_SUBSCRIPTION')
  },
}

export default {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions,
}
